import * as THREE from 'three';
import type { AssetManager } from '../assets/AssetManager';
import type { AssetId } from '../assets/types';
import type { Terrain } from '../terrain/Terrain';
import type { VegetationField, VegetationInstanceDef } from './Vegetation';

/**
 * Turns VegetationField data into actual scene objects using instanced
 * rendering: every instance sharing a model id is drawn with ONE
 * THREE.InstancedMesh per mesh-in-that-GLTF, not one Object3D per tree.
 * That's what makes "thousands of trees" a single draw call per model
 * instead of thousands of individual ones.
 *
 * Geometry/material come straight from AssetManager's cached GLTF and are
 * shared, unmodified, across every instance — only each instance's
 * transform (baked into the InstancedMesh's per-instance matrix buffer)
 * differs. Placement height is sampled from the real terrain
 * (`terrain.getHeightAt`) rather than trusted from each instance's
 * authored Y — VegetationField data may be generated before terrain
 * relief is known (see BiomeRenderer), so terrain is the source of truth
 * for how high off the ground an instance actually sits.
 */
export class VegetationRenderer {
  private instancedMeshes: THREE.InstancedMesh[] = [];

  async realize(
    field: VegetationField,
    assets: AssetManager,
    terrain: Terrain,
    parent: THREE.Object3D,
  ): Promise<void> {
    const instancesByModel = groupByModelId(field.all);

    for (const [modelId, instances] of instancesByModel) {
      const meshTemplates = await this.loadMeshTemplates(modelId, assets);
      for (const template of meshTemplates) {
        const instancedMesh = this.buildInstancedMesh(modelId, template, instances, terrain);
        parent.add(instancedMesh);
        this.instancedMeshes.push(instancedMesh);
      }
    }
  }

  private async loadMeshTemplates(
    modelId: AssetId,
    assets: AssetManager,
  ): Promise<
    Array<{
      geometry: THREE.BufferGeometry;
      material: THREE.Material | THREE.Material[];
      localMatrix: THREE.Matrix4;
    }>
  > {
    if (modelId === 'tree.conifer.proxy') {
      return buildProceduralTreeTemplates();
    }

    const gltf = await assets.models.load(modelId);
    gltf.scene.updateWorldMatrix(true, true);

    const templates: Array<{
      geometry: THREE.BufferGeometry;
      material: THREE.Material | THREE.Material[];
      localMatrix: THREE.Matrix4;
    }> = [];

    gltf.scene.traverse((object) => {
      if (object instanceof THREE.Mesh) {
        templates.push({
          geometry: object.geometry,
          material: object.material,
          // Bakes the mesh's transform *within the GLTF* (relative to the
          // GLTF's own root) into each instance matrix, so a model authored
          // with an offset/rotated mesh node still places correctly.
          localMatrix: object.matrixWorld.clone(),
        });
      }
    });

    return templates;
  }

  private buildInstancedMesh(
    modelId: AssetId,
    template: {
      geometry: THREE.BufferGeometry;
      material: THREE.Material | THREE.Material[];
      localMatrix: THREE.Matrix4;
    },
    instances: VegetationInstanceDef[],
    terrain: Terrain,
  ): THREE.InstancedMesh {
    const instancedMesh = new THREE.InstancedMesh(
      template.geometry,
      template.material,
      instances.length,
    );
    instancedMesh.castShadow = true;
    instancedMesh.receiveShadow = true;
    instancedMesh.name = `Vegetation:${modelId}`;

    const placement = new THREE.Matrix4();
    const composed = new THREE.Matrix4();
    const quaternion = new THREE.Quaternion();
    const scaleVec = new THREE.Vector3();
    const groundPosition = new THREE.Vector3();

    instances.forEach((instance, index) => {
      quaternion.setFromEuler(new THREE.Euler(0, instance.rotationY, 0));
      scaleVec.setScalar(instance.scale);
      const groundY = terrain.getHeightAt(instance.position.x, instance.position.z);
      groundPosition.set(instance.position.x, groundY, instance.position.z);
      placement.compose(groundPosition, quaternion, scaleVec);
      composed.multiplyMatrices(placement, template.localMatrix);
      instancedMesh.setMatrixAt(index, composed);
    });
    instancedMesh.instanceMatrix.needsUpdate = true;

    return instancedMesh;
  }

  dispose(): void {
    for (const mesh of this.instancedMeshes) {
      mesh.parent?.remove(mesh);
      mesh.dispose(); // frees the instance-matrix GPU buffer only; geometry/material are shared/cached elsewhere and must not be disposed here
    }
    this.instancedMeshes = [];
  }
}

function groupByModelId(
  instances: readonly VegetationInstanceDef[],
): Map<AssetId, VegetationInstanceDef[]> {
  const map = new Map<AssetId, VegetationInstanceDef[]>();
  for (const instance of instances) {
    const list = map.get(instance.modelId);
    if (list) list.push(instance);
    else map.set(instance.modelId, [instance]);
  }
  return map;
}

function buildProceduralTreeTemplates(): Array<{
  geometry: THREE.BufferGeometry;
  material: THREE.Material;
  localMatrix: THREE.Matrix4;
}> {
  const bark = new THREE.MeshStandardMaterial({
    color: 0x5a3d27,
    roughness: 0.88,
    metalness: 0,
  });
  const needles = new THREE.MeshStandardMaterial({
    color: 0x1f3b24,
    roughness: 0.96,
    metalness: 0,
  });
  const shadowNeedles = new THREE.MeshStandardMaterial({
    color: 0x162b1c,
    roughness: 0.98,
    metalness: 0,
  });

  return [
    {
      geometry: taperedCylinder(0.16, 0.09, 2.5, 7),
      material: bark,
      localMatrix: new THREE.Matrix4().makeTranslation(0, 1.25, 0),
    },
    {
      geometry: irregularCone(1.05, 2.4, 9, 0.16),
      material: shadowNeedles,
      localMatrix: new THREE.Matrix4().makeTranslation(0, 3.0, 0),
    },
    {
      geometry: irregularCone(0.82, 2.15, 10, 0.14),
      material: needles,
      localMatrix: new THREE.Matrix4().makeTranslation(0.08, 4.0, -0.04),
    },
    {
      geometry: irregularCone(0.56, 1.65, 9, 0.12),
      material: needles,
      localMatrix: new THREE.Matrix4().makeTranslation(-0.05, 4.92, 0.05),
    },
  ];
}

function taperedCylinder(
  bottomRadius: number,
  topRadius: number,
  height: number,
  segments: number,
): THREE.BufferGeometry {
  const geometry = new THREE.CylinderGeometry(topRadius, bottomRadius, height, segments, 2);
  const position = geometry.attributes.position as THREE.BufferAttribute;
  const vertex = new THREE.Vector3();
  for (let i = 0; i < position.count; i++) {
    vertex.fromBufferAttribute(position, i);
    const wobble = 1 + (pseudoRandom(vertex.x * 13, vertex.y * 7, vertex.z * 11) - 0.5) * 0.08;
    position.setXYZ(i, vertex.x * wobble, vertex.y, vertex.z * wobble);
  }
  geometry.computeVertexNormals();
  return geometry;
}

function irregularCone(
  radius: number,
  height: number,
  segments: number,
  asymmetry: number,
): THREE.BufferGeometry {
  const geometry = new THREE.ConeGeometry(radius, height, segments, 3);
  const position = geometry.attributes.position as THREE.BufferAttribute;
  const vertex = new THREE.Vector3();
  for (let i = 0; i < position.count; i++) {
    vertex.fromBufferAttribute(position, i);
    const taper = 1 - Math.max(0, vertex.y + height / 2) / height;
    const n = pseudoRandom(
      Math.round(vertex.x * 10),
      Math.round(vertex.y * 10),
      Math.round(vertex.z * 10),
    );
    const swayX = Math.sin(vertex.y * 2.1) * asymmetry * taper;
    const swayZ = Math.cos(vertex.y * 1.7) * asymmetry * taper;
    position.setXYZ(
      i,
      vertex.x * (0.86 + n * 0.28) + swayX,
      vertex.y,
      vertex.z * (0.9 + n * 0.22) + swayZ,
    );
  }
  geometry.computeVertexNormals();
  return geometry;
}

function pseudoRandom(x: number, y: number, z: number): number {
  const s = Math.sin(x * 12.9898 + y * 78.233 + z * 37.719) * 43758.5453;
  return s - Math.floor(s);
}
